import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

/** `localStorage` key of the Zoom hosts this browser used last. Exported so the Organization
 * switch flow can clear it: the ids belong to one Organization's Zoom integration. */
export const LAST_USED_ZOOM_USER_IDS_STORAGE_KEY = 'LAST_USED_ZOOM_USER_IDS';
const MAX_LAST_USED_ZOOM_USER_IDS = 5;

/** Actions available for managing last used zoom user IDs */
interface LastUsedZoomUserIdsActions {
  /** Add a zoom user ID to the list (moves to front if already exists) */
  add: (userId: string) => void;
  /** Remove a specific zoom user ID from the list */
  remove: (userId: string) => void;
  /** Clear all last used zoom user IDs */
  clear: () => void;
}

type LastUsedZoomUserIdsContextValue = [string[], LastUsedZoomUserIdsActions];

const defaultActions: LastUsedZoomUserIdsActions = {
  add: () => {},
  remove: () => {},
  clear: () => {},
};

const LastUsedZoomUserIdsContext = createContext<LastUsedZoomUserIdsContextValue>([[], defaultActions]);

interface LastUsedZoomUserIdsProviderProps {
  children?: ReactNode;
}

/**
 * Validates if a value is a non-empty string (valid zoom user ID format)
 * @param userId - The value to validate
 * @returns true if the userId is a valid non-empty string
 */
const isValidZoomUserId = (userId: unknown): userId is string => {
  return typeof userId === 'string' && userId.trim() !== '';
};

/**
 * Safely parses and validates zoom user IDs from localStorage
 * @returns An array of valid zoom user ID strings, or empty array if invalid/not found
 */
const restoreZoomUserIds = (): string[] => {
  try {
    const storedData = window.localStorage.getItem(LAST_USED_ZOOM_USER_IDS_STORAGE_KEY);

    if (!storedData) {
      return [];
    }

    const parsed: unknown = JSON.parse(storedData);

    // Validate that parsed data is an array
    if (!Array.isArray(parsed)) {
      console.warn('LastUsedZoomUserIdsProvider: Invalid data format in localStorage, expected array');
      return [];
    }

    // Filter to only valid non-empty strings
    const validUserIds = parsed.filter(isValidZoomUserId);

    // If we had to filter out invalid entries, update localStorage
    if (validUserIds.length !== parsed.length) {
      console.warn('LastUsedZoomUserIdsProvider: Filtered out invalid entries from localStorage');
      try {
        window.localStorage.setItem(LAST_USED_ZOOM_USER_IDS_STORAGE_KEY, JSON.stringify(validUserIds));
      } catch {
        // Ignore storage errors during cleanup
      }
    }

    // Ensure we don't exceed max limit
    return validUserIds.slice(0, MAX_LAST_USED_ZOOM_USER_IDS);
  } catch (err) {
    // JSON.parse failed or other error
    console.warn('LastUsedZoomUserIdsProvider: Failed to parse localStorage data', err);
    return [];
  }
};

/**
 * Safely stores zoom user IDs to localStorage
 * @param userIds - Array of zoom user ID strings to store
 */
const storeZoomUserIds = (userIds: string[]): void => {
  try {
    window.localStorage.setItem(LAST_USED_ZOOM_USER_IDS_STORAGE_KEY, JSON.stringify(userIds));
  } catch (err) {
    // localStorage might be full or disabled
    console.warn('LastUsedZoomUserIdsProvider: Failed to save to localStorage', err);
  }
};

/**
 * Provider component for managing last used zoom user IDs with localStorage persistence.
 *
 * @example
 * ```tsx
 * <LastUsedZoomUserIdsProvider>
 *   <App />
 * </LastUsedZoomUserIdsProvider>
 * ```
 */
export const LastUsedZoomUserIdsProvider: FC<LastUsedZoomUserIdsProviderProps> = ({ children }) => {
  const [userIds, setUserIds] = useState<string[]>([]);

  // Initialize from localStorage on mount
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    const restored = restoreZoomUserIds();
    setUserIds(restored);
  }, []);

  const add = useCallback((userId: string) => {
    // Validate the userId before adding
    if (!isValidZoomUserId(userId)) {
      console.warn(`LastUsedZoomUserIdsProvider: Attempted to add invalid zoom user ID "${userId}"`);
      return;
    }

    setUserIds((prev) => {
      // Create new array
      const updated = [...prev];

      // Check if userId already exists
      const index = updated.indexOf(userId);

      // If it's already at the front, no change needed
      if (index === 0) {
        return prev;
      }

      // Remove from current position if exists
      if (index !== -1) {
        updated.splice(index, 1);
      }

      // Add to front
      updated.unshift(userId);

      // Truncate to max limit
      const result = updated.slice(0, MAX_LAST_USED_ZOOM_USER_IDS);

      // Persist to localStorage
      storeZoomUserIds(result);

      return result;
    });
  }, []);

  const remove = useCallback((userId: string) => {
    if (!isValidZoomUserId(userId)) {
      return;
    }

    setUserIds((prev) => {
      const index = prev.indexOf(userId);

      // If not found, no change needed
      if (index === -1) {
        return prev;
      }

      const updated = [...prev];
      updated.splice(index, 1);

      // Persist to localStorage
      storeZoomUserIds(updated);

      return updated;
    });
  }, []);

  const clear = useCallback(() => {
    setUserIds([]);
    storeZoomUserIds([]);
  }, []);

  const actions = useMemo<LastUsedZoomUserIdsActions>(
    () => ({
      add,
      remove,
      clear,
    }),
    [add, remove, clear]
  );

  const value = useMemo<LastUsedZoomUserIdsContextValue>(() => [userIds, actions], [userIds, actions]);

  return <LastUsedZoomUserIdsContext.Provider value={value}>{children}</LastUsedZoomUserIdsContext.Provider>;
};

/**
 * Hook to access and manage last used zoom user IDs.
 *
 * @returns A tuple containing:
 * - `lastUsedZoomUserIds`: Array of zoom user ID strings (always at least an empty array, never null/undefined)
 * - `actions`: Object with `add`, `remove`, and `clear` functions
 *
 * @example
 * ```tsx
 * const [lastUsedZoomUserIds, { add, remove, clear }] = useLastUsedZoomUserIds();
 *
 * // Add a zoom user ID (moves to front if already exists)
 * add('zoom-user-123');
 *
 * // Remove a specific zoom user ID
 * remove('zoom-user-456');
 *
 * // Clear all zoom user IDs
 * clear();
 * ```
 */
export const useLastUsedZoomUserIds = (): LastUsedZoomUserIdsContextValue => {
  const context = useContext(LastUsedZoomUserIdsContext);

  if (context === undefined) {
    // Return safe default if used outside provider (shouldn't happen but be resilient)
    console.warn('useLastUsedZoomUserIds must be used within a LastUsedZoomUserIdsProvider');
    return [[], defaultActions];
  }

  return context;
};
